/**
 *  Copyright (c) 2026 TechDivision GmbH
 *  All rights reserved
 *
 *  This product includes proprietary software developed at TechDivision GmbH, Germany
 *  For more information see https://www.techdivision.com/
 *
 *  To obtain a valid license for using this software please contact us at
 *  license@techdivision.com
 *
 */

/* autoplay-video.js
   Pause control for background videos that start on their own (hero banner).
   WCAG 2.2.2 requires moving content lasting longer than five seconds to be
   stoppable, and the videos render without native controls.

   Also honours prefers-reduced-motion: the video stays on its first frame and
   the control offers to play it instead. */

export function initAutoplayVideo() {
  var wrappers = Array.prototype.slice.call(document.querySelectorAll('[data-autoplay-video]'));
  if (!wrappers.length) return;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  wrappers.forEach(function (wrapper) {
    var video = wrapper.querySelector('video');
    var toggle = wrapper.querySelector('[data-video-toggle]');
    if (!video || !toggle) return;

    var hasPlayed = false;

    // the markup ships in the playing state (pause icon), so the control is only
    // repainted once the state actually changes
    function render(paused) {
      toggle.classList.toggle('is-paused', paused);
      var label = paused ? toggle.getAttribute('data-label-play') : toggle.getAttribute('data-label-pause');
      if (label) toggle.setAttribute('aria-label', label);
    }

    function sync() {
      render(video.paused);
    }

    if (reduceMotion) {
      // remove the attribute as well, otherwise the browser starts playback
      // again as soon as the video has buffered enough
      video.removeAttribute('autoplay');
      video.autoplay = false;
      video.pause();
      render(true);
    }

    toggle.addEventListener('click', function () {
      if (video.paused) {
        var played = video.play();
        // Safari rejects play() in a background tab; keep the label truthful
        if (played && typeof played.catch === 'function') played.catch(sync);
      } else {
        video.pause();
      }
    });

    // the browser can pause on its own (data saver, background tab, low power
    // mode), so the label follows the element instead of the last click
    video.addEventListener('play', function () {
      hasPlayed = true;
      render(false);
    });
    video.addEventListener('pause', function () {
      render(true);
    });
    // a video that reached its end without looping shows nothing moving, no
    // matter what the element reports for `paused` – the next click restarts it
    video.addEventListener('ended', function () {
      render(true);
    });

    // by the time the video could play, autoplay has either started or was
    // refused – only then is it worth correcting the initial state
    video.addEventListener('canplay', function () {
      if (!hasPlayed) sync();
    });
  });
}

// loaded as a module script by the section that needs it (see
// snippets/module-script.liquid), so the module initialises itself. module
// scripts are deferred, which means the DOM is complete at this point
initAutoplayVideo();
